// Pending money operations retain their exact payload and UUID across uncertain
// responses/reloads. They are only offered for explicit retry, never auto-sent.
export interface FinanceRequest {
  action: string;
  id: string;
  requestId: string;
  [field: string]: unknown;
}
type RequestStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const KEY = "life-os:pending-finance";
const LABELS: Record<string, string> = { income: "Income", expense: "Expense", transfer: "Transfer", account: "Account" };
export const financeLabel = (request: FinanceRequest): string => LABELS[request.action] || "Financial operation";

export function pendingFinanceRequests(storage: RequestStorage = sessionStorage): FinanceRequest[] {
  try {
    const value: unknown = JSON.parse(storage.getItem(KEY) || "[]");
    return Array.isArray(value) ? value.filter((r): r is FinanceRequest => !!r && typeof r === "object"
      && typeof r.id === "string" && typeof r.requestId === "string" && typeof r.action === "string"
      && Object.hasOwn(LABELS, r.action)) : [];
  } catch { return []; }
}

export function retainFinanceRequest(input: Omit<FinanceRequest, "requestId">, storage: RequestStorage = sessionStorage, createId = () => crypto.randomUUID()): FinanceRequest {
  const pending = pendingFinanceRequests(storage);
  const previous = pending.find(r => r.action === input.action && r.id === input.id);
  // Never replace the payload of an uncertain operation with edited form data.
  if (previous) return previous;
  if (pending.length >= 100) throw new Error("Retry your unsaved financial changes before adding another.");
  const request = { ...input, requestId: createId() } as FinanceRequest;
  storage.setItem(KEY, JSON.stringify([...pending, request]));
  return request;
}

export function confirmFinanceRequest(request: FinanceRequest, storage: RequestStorage = sessionStorage): void {
  const remaining = pendingFinanceRequests(storage).filter(r => r.requestId !== request.requestId);
  if (remaining.length) storage.setItem(KEY, JSON.stringify(remaining));
  else storage.removeItem(KEY);
}

export function clearFinanceRequests(storage: RequestStorage = sessionStorage): void { storage.removeItem(KEY); }
